/**
 * MessagesCard Component
 *
 * Dashboard card for viewing recent direct messages.
 * Shows recent conversations with unread badges, message previews,
 * and actions to start new conversations.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  Clock,
  ChevronRight,
  Mail,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useConversations,
  useUnreadCount,
  useMessagingLoading,
  useMessagingActions,
} from '@/stores/messaging';
import { NewConversationModal } from '@/components/messages/NewConversationModal';

import type { Conversation } from '@/types/messaging';

/**
 * Get initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

const getGroupInitials = (name?: string): string => {
  if (!name) return 'G';

  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
  }

  return words[0]?.charAt(0).toUpperCase() || 'G';
};

/**
 * Get display metadata for a conversation
 */
const getConversationDisplay = (conversation: Conversation) => {
  if (conversation.isGroup) {
    const participantNames = conversation.participants
      ?.map((participant) => {
        if (participant.firstName && participant.lastName) {
          return `${participant.firstName} ${participant.lastName}`;
        }
        return participant.firstName || participant.username;
      })
      .filter(Boolean);

    const displayName = conversation.groupName || participantNames?.join(', ') || 'Group conversation';

    return {
      displayName,
      initials: getGroupInitials(displayName),
      avatar: conversation.groupAvatar,
      isOnline: false,
    };
  }

  const { participant } = conversation;
  if (!participant) {
    return {
      displayName: 'Unknown',
      initials: getInitials(),
      avatar: undefined,
      isOnline: false,
    };
  }

  let displayName: string;
  if (participant.firstName && participant.lastName) {
    displayName = `${participant.firstName} ${participant.lastName}`;
  } else if (participant.firstName) {
    displayName = participant.firstName;
  } else {
    displayName = participant.username;
  }

  return {
    displayName,
    initials: getInitials(participant.firstName, participant.lastName, participant.username),
    avatar: participant.avatar,
    isOnline: Boolean(participant.isOnline),
  };
};

/**
 * Format message timestamp
 */
const formatMessageTime = (dateString?: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
};

/**
 * Truncate message preview
 */
const truncateMessage = (content?: string, maxLength: number = 40): string => {
  if (!content) return 'No messages yet';
  if (content.length <= maxLength) return content;
  return content.substring(0, maxLength).trim() + '...';
};

/**
 * ConversationRow - Individual conversation row
 */
interface ConversationRowProps {
  conversation: Conversation;
  onClick: () => void;
}

const ConversationRow: React.FC<ConversationRowProps> = ({ conversation, onClick }) => {
  const { displayName, initials, avatar, isOnline } = getConversationDisplay(conversation);

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-3 py-2.5 w-full text-left hover:bg-muted/50 rounded-lg px-2 -mx-2 transition-colors"
      aria-label={`Open conversation with ${displayName}`}
    >
      <div className="relative">
        <Avatar className="h-9 w-9">
          <AvatarImage src={avatar} alt={displayName} />
          <AvatarFallback
            style={{
              backgroundColor: 'hsl(var(--primary) / 0.1)',
              color: 'hsl(var(--primary))',
            }}
            className="text-xs font-medium"
          >
            {initials}
          </AvatarFallback>
        </Avatar>
        {/* Online indicator */}
        {isOnline && (
          <span
            className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-card"
            style={{ backgroundColor: 'hsl(var(--primary))' }}
            aria-label="Online"
          />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className={`text-sm truncate ${conversation.unreadCount > 0 ? 'font-semibold' : 'font-medium'}`}>
            {displayName}
          </p>
          {conversation.unreadCount > 0 && (
            <Badge
              className="h-5 min-w-[20px] px-1.5 text-xs shrink-0"
              style={{
                backgroundColor: 'hsl(var(--primary))',
                color: 'hsl(var(--primary-foreground))',
              }}
            >
              {conversation.unreadCount}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1">
          <p className={`text-xs truncate flex-1 ${conversation.unreadCount > 0 ? 'text-foreground' : 'text-muted-foreground'}`}>
            {truncateMessage(conversation.lastMessage?.content)}
          </p>
          {conversation.lastMessageAt && (
            <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatMessageTime(conversation.lastMessageAt)}
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

/**
 * Loading skeleton for conversations
 */
const ConversationsSkeleton: React.FC = () => (
  <div className="space-y-2">
    {[1, 2, 3].map((i) => (
      <div key={i} className="flex items-center gap-3 py-2.5">
        <Skeleton className="h-9 w-9 rounded-full" />
        <div className="flex-1">
          <Skeleton className="h-4 w-24 mb-1.5" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-3 w-12" />
      </div>
    ))}
  </div>
);

/**
 * MessagesCard - Main dashboard card component
 */
export const MessagesCard: React.FC = () => {
  const navigate = useNavigate();
  const conversations = useConversations();
  const totalUnreadCount = useUnreadCount();
  const { isLoadingConversations } = useMessagingLoading();
  const { fetchConversations, fetchUnreadCount } = useMessagingActions();

  const [showNewMessageModal, setShowNewMessageModal] = useState(false);

  // Fetch conversations and unread count on mount
  useEffect(() => {
    fetchConversations();
    fetchUnreadCount();
  }, [fetchConversations, fetchUnreadCount]);

  // Get recent conversations (max 5)
  const recentConversations = conversations
    .filter((c) => !c.archivedAt) // Exclude archived
    .slice(0, 5);
  const hasMoreConversations = conversations.filter((c) => !c.archivedAt).length > 5;

  const handleConversationClick = (conversationId: string) => {
    navigate(`/dashboard/messages?conversation=${conversationId}`);
  };

  return (
    <>
      <Card className="h-full flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              <CardTitle>Messages</CardTitle>
              {/* Total unread badge */}
              {totalUnreadCount > 0 && (
                <Badge
                  variant="default"
                  className="ml-2 h-5 min-w-[20px] px-1.5 text-xs"
                  style={{
                    backgroundColor: 'hsl(var(--primary))',
                    color: 'hsl(var(--primary-foreground))',
                  }}
                >
                  {totalUnreadCount}
                </Badge>
              )}
            </div>

            {/* View All button */}
            <Button
              variant="ghost"
              size="sm"
              className="text-sm text-muted-foreground hover:text-foreground"
              onClick={() => navigate('/dashboard/messages')}
            >
              View All
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <CardDescription>
            Direct and group conversations
          </CardDescription>
        </CardHeader>

        <CardContent className="flex-1">
          {isLoadingConversations ? (
            <ConversationsSkeleton />
          ) : recentConversations.length === 0 ? (
            /* Empty state */
            <div className="text-center py-6">
              <div
                className="h-12 w-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                style={{
                  backgroundColor: 'hsl(var(--primary) / 0.1)',
                }}
              >
                <Mail className="h-6 w-6" style={{ color: 'hsl(var(--primary))' }} />
              </div>
              <p className="text-sm font-medium mb-1">No messages yet</p>
              <p className="text-xs text-muted-foreground mb-4">
                Start a conversation with one of your connections
              </p>
              <Button
                onClick={() => {
                  setShowNewMessageModal(true);
                }}
                size="sm"
                style={{
                  backgroundColor: 'hsl(var(--primary))',
                  color: 'hsl(var(--primary-foreground))',
                }}
              >
                <Send className="h-4 w-4 mr-2" />
                New Message
              </Button>
            </div>
          ) : (
            /* Conversations list */
            <div className="space-y-1">
              <div className="space-y-1 max-h-[180px] overflow-y-auto">
                {recentConversations.map((conversation) => (
                  <ConversationRow
                    key={conversation.id}
                    conversation={conversation}
                    onClick={() => handleConversationClick(conversation.id)}
                  />
                ))}

                {hasMoreConversations && (
                  <p className="text-xs text-muted-foreground text-center pt-2">
                    And {conversations.filter((c) => !c.archivedAt).length - 5} more...
                  </p>
                )}
              </div>

              {/* New message button */}
              <div className="pt-3">
                <Button
                  onClick={() => {
                    setShowNewMessageModal(true);
                  }}
                  variant="outline"
                  className="w-full"
                  size="sm"
                >
                  <Send className="h-4 w-4 mr-2" />
                  New Message
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Conversation Modal */}
      <NewConversationModal
        open={showNewMessageModal}
        onOpenChange={setShowNewMessageModal}
      />
    </>
  );
};

export default MessagesCard;
